/**
 * Maps raw scan-failure signals from the backend (SSE `error` payloads and
 * `GET /api/scan-status/:id` failed responses) to a failure category and
 * human-readable copy with a constructive next step. Issue #364: the hero
 * widget must never show a bare "Scan failed".
 */

export type ScanFailureCategory =
  | "bot_blocked"
  | "timeout"
  | "unreachable"
  | "unknown";

export interface ScanFailureDescription {
  category: ScanFailureCategory;
  message: string;
}

const BOT_BLOCK_STEP =
  "If you manage this site's bot-blocking or WAF, allowlist our scanner's IP ranges (74.220.48.0/24 and 74.220.56.0/24) and try again.";

const RETRY_STEP =
  "Try scanning a different page of the same site, or try again in a moment.";

const BOT_BLOCK_PATTERNS =
  /bot protection|block\/challenge|blocked|captcha|returned http (401|403|405|406|429|5\d\d)/i;

const TIMEOUT_PATTERNS = /took too long|timed? ?out|timeout \d+ms exceeded/i;

const UNREACHABLE_PATTERNS =
  /net::err_|err_name_not_resolved|err_connection|could not resolve|unreachable|invalid url|getaddrinfo|enotfound|econnrefused|returned http 404/i;

export function describeScanFailure(
  error?: string | null,
  blockedBySite?: boolean,
): ScanFailureDescription {
  const raw = (error ?? "").trim();

  if (blockedBySite || BOT_BLOCK_PATTERNS.test(raw)) {
    return {
      category: "bot_blocked",
      message: `This site appears to block automated scanners — our auditor was stopped by its bot protection, not by a problem with your URL. ${BOT_BLOCK_STEP}`,
    };
  }

  if (TIMEOUT_PATTERNS.test(raw)) {
    return {
      category: "timeout",
      message: `The scan timed out before finishing. Sites with heavy JavaScript or slow responses can exceed our free-scan time limit. Please try again in a moment.`,
    };
  }

  if (UNREACHABLE_PATTERNS.test(raw)) {
    return {
      category: "unreachable",
      message:
        "We couldn't reach that address. Double-check the URL (including any typos in the domain) and try again.",
    };
  }

  return {
    category: "unknown",
    message: raw
      ? `${raw} ${RETRY_STEP}`
      : `The scan failed for an unexpected reason. ${RETRY_STEP}`,
  };
}
